/**
 * TenantIntegrationTab — Head script + Meta Pixel + XML sync & scheduler.
 *
 * Backend endpoint'leri:
 *   POST /admin/tenants/{id}/install/test-head
 *   POST /admin/tenants/{id}/install/test-meta
 *   PUT  /admin/tenants/{id}/xml/url                { url }
 *   POST /admin/tenants/{id}/xml/inspect
 *   POST /admin/tenants/{id}/xml/sync-start          { mapping, category_delimiter, item_path }
 *   PUT  /admin/tenants/{id}/xml/schedule            { enabled, preset, times, every_x, weekdays }
 *
 * Scheduler preset UI (TR):
 *   • Günlük (tek saat / birden fazla saat virgülle)
 *   • Her X Günde Bir
 *   • Haftanın Günleri (Pzt/Sal/Çar/… multi-pick)
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Card, CardHeader, Eyebrow } from "@/components/design-system";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
    FileCode2, Globe, Loader2, CheckCircle2, XCircle, AlertTriangle,
    PlayCircle, Clock, CalendarDays, FileText, RefreshCw, Pencil, Save,
    LinkIcon,
} from "lucide-react";
import { fmtDateTime, relTime, readError } from "../_tenant-helpers";

const WEEKDAY_LABELS = [
    { v: 1, l: "Pzt" },
    { v: 2, l: "Sal" },
    { v: 3, l: "Çar" },
    { v: 4, l: "Per" },
    { v: 5, l: "Cum" },
    { v: 6, l: "Cts" },
    { v: 0, l: "Paz" },
];

export default function TenantIntegrationTab({ data, onRefresh }) {
    return (
        <div className="space-y-5" data-testid="integration-tab">
            <InstallSection data={data} onRefresh={onRefresh} />
            <XmlSection data={data} onRefresh={onRefresh} />
            <ScheduleSection data={data} onRefresh={onRefresh} />
            <RecentJobsSection jobs={data.recent_jobs || []} />
        </div>
    );
}

/* -------------------- Install (Head + Meta) -------------------- */
function InstallSection({ data, onRefresh }) {
    const [testingHead, setTestingHead] = useState(false);
    const [testingMeta, setTestingMeta] = useState(false);
    const inst = data.install || {};

    const testHead = async () => {
        setTestingHead(true);
        try {
            const { data: r } = await adminApi.post(`/admin/tenants/${data.id}/install/test-head`);
            if (r?.ok) toast.success("Head script bağlantısı başarılı.");
            else toast.error(r?.reason || "Head script bulunamadı.");
            onRefresh?.();
        } catch (err) { toast.error(readError(err)); }
        finally { setTestingHead(false); }
    };

    const testMeta = async () => {
        setTestingMeta(true);
        try {
            const { data: r } = await adminApi.post(`/admin/tenants/${data.id}/install/test-meta`);
            if (r?.ok) toast.success("SKU etiketi test edildi.");
            else toast.error(r?.reason || "SKU etiketi bulunamadı.");
            onRefresh?.();
        } catch (err) { toast.error(readError(err)); }
        finally { setTestingMeta(false); }
    };

    return (
        <Card>
            <CardHeader
                title="Site Kurulumu"
                desc="Head scripti ve ürün SKU etiketini süperadmin tarafından test et."
            />
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <InstallCard
                    icon={FileCode2}
                    title="Head Script"
                    status={inst.head_status}
                    lastAt={inst.head_last_at}
                    reason={inst.head_reason}
                    busy={testingHead}
                    onTest={testHead}
                    testId="install-head"
                />
                <InstallCard
                    icon={Globe}
                    title="Ürün SKU Etiketi"
                    status={inst.meta_status}
                    lastAt={inst.meta_last_at}
                    reason={inst.meta_reason}
                    busy={testingMeta}
                    onTest={testMeta}
                    testId="install-meta"
                    sub={
                        (inst.meta_results || []).length > 0 && (
                            <div className="mt-2 space-y-1">
                                {inst.meta_results.map((r, i) => (
                                    <div key={i} className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                        {r.found
                                            ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" strokeWidth={2} />
                                            : <XCircle className="h-3 w-3 text-rose-600 shrink-0" strokeWidth={2} />}
                                        <span className="truncate flex-1">{r.url}</span>
                                        {r.sku && <span className="font-mono text-[10px]">{r.sku}</span>}
                                    </div>
                                ))}
                            </div>
                        )
                    }
                />
            </div>
        </Card>
    );
}

function InstallCard({ icon: Icon, title, status, lastAt, reason, busy, onTest, sub, testId }) {
    const map = {
        connected:    { color: "text-emerald-600", bg: "bg-emerald-100 border-emerald-300", label: "Bağlı", IconC: CheckCircle2 },
        disconnected: { color: "text-rose-600",    bg: "bg-rose-100 border-rose-300",       label: "Kopuk", IconC: XCircle },
        unknown:      { color: "text-muted-foreground", bg: "bg-muted border-border",       label: "Bilinmiyor", IconC: AlertTriangle },
    };
    const m = map[status] || map.unknown;
    const IconC = m.IconC;
    return (
        <div className="rounded-lg border border-border bg-background p-4" data-testid={testId}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                    <span className="text-sm font-semibold">{title}</span>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${m.bg} ${m.color}`}>
                    <IconC className="h-3 w-3" strokeWidth={2} />
                    {m.label}
                </span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
                {lastAt ? `Son kontrol ${relTime(lastAt)}` : "Henüz test edilmedi"}
            </div>
            {reason && (
                <div className="text-[11px] text-muted-foreground italic mt-0.5">{reason}</div>
            )}
            {sub}
            <Button
                variant="outline"
                size="sm"
                onClick={onTest}
                disabled={busy}
                className="h-7 text-[11px] mt-3"
                data-testid={`${testId}-test-btn`}
            >
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" strokeWidth={1.75} />}
                Yeniden Test Et
            </Button>
        </div>
    );
}

/* -------------------- XML url + mapping -------------------- */
function XmlSection({ data, onRefresh }) {
    const xml = data.xml || {};
    const [editing, setEditing] = useState(!xml.url);
    const [url, setUrl] = useState(xml.url || "");
    const [saving, setSaving] = useState(false);
    const [inspecting, setInspecting] = useState(false);
    const [inspect, setInspect] = useState(null);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        setUrl(xml.url || "");
        setEditing(!xml.url);
    }, [xml.url]);

    const save = async () => {
        if (!url.trim()) return;
        setSaving(true);
        try {
            await adminApi.put(`/admin/tenants/${data.id}/xml/url`, { url: url.trim() });
            toast.success("XML URL kaydedildi.");
            setEditing(false);
            onRefresh?.();
        } catch (err) { toast.error(readError(err)); }
        finally { setSaving(false); }
    };

    const runInspect = async () => {
        setInspecting(true);
        try {
            const { data: r } = await adminApi.post(`/admin/tenants/${data.id}/xml/inspect`);
            setInspect(r);
            toast.success(`${r?.sample_paths?.length || 0} alan tespit edildi.`);
        } catch (err) { toast.error(readError(err)); }
        finally { setInspecting(false); }
    };

    const startSync = async () => {
        const mapping = xml.mapping || inspect?.suggested_mapping;
        const item_path = xml.item_path || inspect?.item_path;
        if (!mapping || !item_path) {
            toast.error("Önce 'Alanları Keşfet' çalıştır.");
            return;
        }
        if (!mapping.sku) {
            toast.error("Mapping'de 'sku' alanı eşlenmemiş. 'Alanları Keşfet' sonrası sku yolunu seç.");
            return;
        }
        setSyncing(true);
        try {
            await adminApi.post(`/admin/tenants/${data.id}/xml/sync-start`, {
                mapping,
                category_delimiter: xml.category_delimiter || ">",
                item_path,
            });
            toast.success("Senkron başlatıldı.");
            onRefresh?.();
        } catch (err) { toast.error(readError(err)); }
        finally { setSyncing(false); }
    };

    return (
        <Card>
            <CardHeader
                title="XML Beslemesi"
                desc="Ürün kataloğunu çeken RSS/XML feed. Mapping alanları otomatik tespit edilir."
            />
            <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-md border border-border bg-muted/40 grid place-items-center shrink-0 mt-0.5">
                        <FileText className="h-4 w-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <Eyebrow>Feed URL</Eyebrow>
                        {editing ? (
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://.../products.xml"
                                    className="h-9 text-xs flex-1"
                                    data-testid="xml-url-input"
                                />
                                <Button
                                    onClick={save}
                                    disabled={!url.trim() || saving}
                                    className="h-9 text-xs bg-foreground text-background hover:bg-foreground/90"
                                    data-testid="xml-url-save"
                                >
                                    {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" strokeWidth={2} />}
                                    Kaydet
                                </Button>
                                {xml.url && (
                                    <Button variant="outline" size="sm" onClick={() => { setUrl(xml.url); setEditing(false); }} className="h-9 text-xs">
                                        Vazgeç
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 mt-1">
                                <code className="flex-1 text-xs font-mono break-all text-muted-foreground">
                                    {xml.url}
                                </code>
                                <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="h-8 text-[11px]" data-testid="xml-url-edit">
                                    <Pencil className="h-3 w-3 mr-1" strokeWidth={1.75} /> Düzenle
                                </Button>
                            </div>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                            <span>Son senk: {xml.last_sync_at ? `${relTime(xml.last_sync_at)} · ${xml.last_sync_status || "—"}` : "hiç çalışmadı"}</span>
                            <span>Ürün: <b className="text-foreground">{xml.total_products || 0}</b></span>
                        </div>
                    </div>
                </div>

                {/* Mapping tespit ve sync */}
                {xml.url && (() => {
                    const effectiveMapping = xml.mapping || inspect?.suggested_mapping;
                    const skuMissing = Boolean(effectiveMapping) && !effectiveMapping?.sku;
                    return (
                    <div className="pt-3 border-t border-border flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={runInspect}
                            disabled={inspecting}
                            className="h-8 text-[11px]"
                            data-testid="xml-inspect"
                        >
                            {inspecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <LinkIcon className="h-3 w-3 mr-1" strokeWidth={1.75} />}
                            Alanları Keşfet
                        </Button>
                        <Button
                            size="sm"
                            onClick={startSync}
                            disabled={syncing || (!xml.mapping && !inspect) || skuMissing}
                            className="h-8 text-[11px] bg-foreground text-background hover:bg-foreground/90"
                            data-testid="xml-sync-start"
                            title={skuMissing ? "SKU alanı eşlenmemiş — tespit sonrası sku bulunamadı" : ""}
                        >
                            {syncing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <PlayCircle className="h-3 w-3 mr-1" strokeWidth={2} />}
                            Hemen Senk Et
                        </Button>
                        {inspect && (
                            <span className="text-[11px] text-muted-foreground ml-2" data-testid="xml-inspect-result">
                                Tespit: item_path = <code className="font-mono">{inspect.item_path}</code>, {inspect.sample_paths?.length || 0} alan.
                            </span>
                        )}
                        {skuMissing && (
                            <span className="text-[11px] text-rose-600 font-medium" data-testid="xml-sku-missing">
                                SKU eşlenmemiş — bu feed için senkron başlatılamaz.
                            </span>
                        )}
                    </div>
                    );
                })()}
            </div>
        </Card>
    );
}

/* -------------------- Scheduler (presetli) -------------------- */
function ScheduleSection({ data, onRefresh }) {
    const sched = data.xml?.schedule || {};
    const initialPreset = sched.preset && sched.preset !== "custom" ? sched.preset : "daily";
    const [enabled, setEnabled] = useState(Boolean(sched.enabled));
    const [preset, setPreset] = useState(initialPreset);
    const [times, setTimes] = useState(sched.preset_payload?.times?.join(", ") || "03:00");
    const [everyX, setEveryX] = useState(sched.preset_payload?.every_x || 1);
    const [weekdays, setWeekdays] = useState(sched.preset_payload?.weekdays || [1, 3, 5]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setEnabled(Boolean(sched.enabled));
        setPreset(sched.preset && sched.preset !== "custom" ? sched.preset : "daily");
        setTimes(sched.preset_payload?.times?.join(", ") || "03:00");
        setEveryX(sched.preset_payload?.every_x || 1);
        setWeekdays(sched.preset_payload?.weekdays || [1, 3, 5]);
    }, [data?.xml?.schedule?.preset, data?.xml?.schedule?.cron]);  // eslint-disable-line

    const parsedTimes = useMemo(() => {
        return times.split(/[,\s]+/).map((t) => t.trim()).filter(Boolean);
    }, [times]);

    const canSave = (() => {
        if (parsedTimes.length === 0) return false;
        if (preset === "every_x_days") return everyX >= 1 && everyX <= 31;
        if (preset === "weekdays") return weekdays.length > 0;
        return true;
    })();

    const save = async () => {
        setSaving(true);
        try {
            const body = { enabled, preset, timezone: "Europe/Istanbul", times: parsedTimes };
            if (preset === "every_x_days") body.every_x = Number(everyX);
            if (preset === "weekdays") body.weekdays = weekdays;
            const { data: r } = await adminApi.put(`/admin/tenants/${data.id}/xml/schedule`, body);
            toast.success(`Zamanlama kaydedildi: ${r.cron}`);
            onRefresh?.();
        } catch (err) { toast.error(readError(err)); }
        finally { setSaving(false); }
    };

    const toggleWeekday = (v) => {
        setWeekdays((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
    };

    return (
        <Card>
            <CardHeader
                title="Zamanlama"
                desc="XML'in otomatik olarak hangi aralıkta çekileceği. Birden fazla saat için virgül kullanabilirsin (örn: 09:00, 15:00)."
                right={
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">Aktif</span>
                        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="schedule-enabled-switch" />
                    </div>
                }
            />
            <div className="p-5 space-y-4" data-testid="schedule-section">
                {/* Preset chips */}
                <div className="flex flex-wrap gap-2">
                    <PresetBtn active={preset === "daily"} onClick={() => setPreset("daily")} icon={Clock} testId="preset-daily">
                        Günlük
                    </PresetBtn>
                    <PresetBtn active={preset === "every_x_days"} onClick={() => setPreset("every_x_days")} icon={CalendarDays} testId="preset-every-x">
                        Her X Günde Bir
                    </PresetBtn>
                    <PresetBtn active={preset === "weekdays"} onClick={() => setPreset("weekdays")} icon={CalendarDays} testId="preset-weekdays">
                        Haftanın Günleri
                    </PresetBtn>
                </div>

                {/* Preset body */}
                <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                    <Field label="Çalışma Saatleri">
                        <Input
                            value={times}
                            onChange={(e) => setTimes(e.target.value)}
                            placeholder="09:00, 15:00"
                            className="h-9 text-xs"
                            data-testid="schedule-times-input"
                        />
                        <div className="text-[10px] text-muted-foreground mt-1">
                            {parsedTimes.length > 0 ? `Önizleme: ${parsedTimes.join(", ")}` : "Örn: 09:00, 15:00"}
                        </div>
                    </Field>

                    {preset === "every_x_days" && (
                        <Field label="Her Kaç Günde Bir">
                            <Input
                                type="number"
                                min={1}
                                max={31}
                                value={everyX}
                                onChange={(e) => setEveryX(Number(e.target.value || 1))}
                                className="h-9 w-28 text-xs"
                                data-testid="schedule-every-x-input"
                            />
                        </Field>
                    )}

                    {preset === "weekdays" && (
                        <Field label="Çalışacağı Günler">
                            <div className="flex flex-wrap gap-1.5">
                                {WEEKDAY_LABELS.map((d) => (
                                    <button
                                        key={d.v}
                                        type="button"
                                        onClick={() => toggleWeekday(d.v)}
                                        data-testid={`weekday-${d.v}`}
                                        className={[
                                            "h-8 px-2.5 rounded-md border text-[11px] font-medium transition-colors",
                                            weekdays.includes(d.v)
                                                ? "bg-foreground text-background border-foreground"
                                                : "bg-background text-muted-foreground border-border hover:border-foreground/40",
                                        ].join(" ")}
                                    >
                                        {d.l}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    )}
                </div>

                {/* Current + next-run */}
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                    <span>Mevcut cron: <code className="font-mono text-foreground">{sched.cron || "—"}</code></span>
                    <span>·</span>
                    <span>Sonraki çalışma: <b className="text-foreground">{sched.next_run_at ? fmtDateTime(sched.next_run_at) : "—"}</b></span>
                    {sched.last_auto_run_at && (
                        <>
                            <span>·</span>
                            <span>Son otomatik: {relTime(sched.last_auto_run_at)}</span>
                        </>
                    )}
                </div>

                <div className="flex justify-end">
                    <Button
                        onClick={save}
                        disabled={!canSave || saving}
                        className="h-9 text-xs bg-foreground text-background hover:bg-foreground/90"
                        data-testid="schedule-save"
                    >
                        {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" strokeWidth={2} />}
                        Zamanlamayı Kaydet
                    </Button>
                </div>
            </div>
        </Card>
    );
}

function PresetBtn({ active, onClick, icon: Icon, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={[
                "inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-[12px] font-medium transition-colors",
                active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-foreground border-border hover:border-foreground/40",
            ].join(" ")}
        >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            {children}
        </button>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}
            </div>
            {children}
        </div>
    );
}

/* -------------------- Recent XML sync jobs -------------------- */
function RecentJobsSection({ jobs }) {
    if (!jobs?.length) return null;
    return (
        <Card>
            <CardHeader title="Son Senkron Çalıştırmaları" desc={`Son ${jobs.length} iş kaydı.`} />
            <div className="p-5">
                <table className="w-full text-sm" data-testid="recent-jobs-table">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b border-border">
                            <th className="pb-2 pr-3">Başlangıç</th>
                            <th className="pb-2 pr-3">Durum</th>
                            <th className="pb-2 pr-3">Görülen / Yazılan</th>
                            <th className="pb-2 pr-3">Arşiv</th>
                            <th className="pb-2">Hata</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((j) => (
                            <tr key={j.job_id} className="border-b border-border last:border-b-0" data-testid={`job-${j.job_id}`}>
                                <td className="py-2 pr-3 text-[12px]">{fmtDateTime(j.started_at)}</td>
                                <td className="py-2 pr-3">
                                    <StatusChip s={j.status} />
                                </td>
                                <td className="py-2 pr-3 text-[12px]">
                                    {j.total_seen || 0} / <b>{j.total_written || 0}</b>
                                </td>
                                <td className="py-2 pr-3 text-[12px]">{j.total_archived || 0}</td>
                                <td className="py-2 text-[11px] text-rose-600 italic">{j.error || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function StatusChip({ s }) {
    const map = {
        success:  { c: "bg-emerald-100 text-emerald-800 border-emerald-200", l: "başarılı" },
        failed:   { c: "bg-rose-100 text-rose-800 border-rose-200", l: "başarısız" },
        queued:   { c: "bg-muted text-muted-foreground border-border", l: "sırada" },
        downloading: { c: "bg-sky-100 text-sky-800 border-sky-200", l: "indiriliyor" },
        parsing:  { c: "bg-sky-100 text-sky-800 border-sky-200", l: "ayrıştırılıyor" },
        writing:  { c: "bg-sky-100 text-sky-800 border-sky-200", l: "yazılıyor" },
    };
    const m = map[s] || { c: "bg-muted text-muted-foreground border-border", l: s || "—" };
    return (
        <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${m.c}`}>
            {m.l}
        </span>
    );
}
